import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Form, Input, InputNumber, message, Modal, Select,
  Space, Switch, Tag, Tooltip, Typography
} from 'antd';
import { EditOutlined, PlusOutlined, FieldNumberOutlined } from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../components/common/erp-grid';
import { accountsApi } from './accountsApi';
import useAuth from '../../../hooks/useAuth';
import './accounts.css';

const { Title, Text } = Typography;

// Stage 6B.2 — Document Sequences page.
//
// Operator-facing CRUD over document_sequences. The Edit dialog is limited to
// prefix, padding, reset frequency, is_active — `currentNumber` stays out of
// it, because changing financial numbering is a different kind of act from
// adjusting a label.
//
// RENUMBER (added later) is that separate act, on its own MASTER-ONLY action.
// It exists because deleting a document frees its number — every numbered
// table's unique index excludes soft-deleted rows — while current_number does
// not rewind, so the next document skips it and leaves a permanent gap.
//
// The dialog never asks the operator to work out a safe value. It reads
// /document-sequences/:id/usage, which reports the highest number a LIVE
// document actually holds, and pre-fills that. The server enforces the same
// floor, so a stale dialog cannot slip a bad value through
// (409 DOC_SEQUENCE_NUMBER_IN_USE).
//
// An Approval-MFA operation key for this action is already reserved as
// `accounts.sequence.reset_number` in accounts.constants.APPROVAL_MFA_OPS,
// still unwired — today the gate is requireMaster on the route.

const ADAPTER = { list: (q) => accountsApi.listDocumentSequences(q) };

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load document sequences.';
  message.error({ content: msg, key: 'doc-seq-list' });
}

const RESET_OPTIONS = [
  { value: 'never',          label: 'Never reset' },
  { value: 'financial_year', label: 'Reset every FY' },
  { value: 'month',          label: 'Reset every month' }
];

function previewNumber(row) {
  if (!row) return '';
  const padded = String((row.currentNumber || 0) + 1).padStart(row.padding || 6, '0');
  return `${row.prefix}-${row.financialYear ? row.financialYear + '/' : ''}${padded}`;
}

function EditSequenceModal({ open, row, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const initial = useMemo(() => ({
    prefix:         row?.prefix || '',
    padding:        row?.padding ?? 6,
    resetFrequency: row?.resetFrequency || 'financial_year',
    isActive:       row?.isActive !== false
  }), [row]);

  const onSubmit = useCallback(async (values) => {
    if (!row) return;
    setBusy(true);
    try {
      const data = await accountsApi.updateDocumentSequence(row.id, {
        prefix:         (values.prefix || '').trim(),
        padding:        Number(values.padding) || 6,
        resetFrequency: values.resetFrequency,
        isActive:       values.isActive === true
      });
      message.success(`Updated sequence ${data.documentType}.`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save sequence.');
    } finally { setBusy(false); }
  }, [row, onSaved, onClose]);

  return (
    <Modal
      title={row ? `Edit Sequence — ${row.documentType}` : 'Edit Sequence'}
      open={open} onCancel={onClose} footer={null} destroyOnClose width={520}
      centered className="erp-accounts-modal"
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 10 }}
        message="Current number is not edited here"
        description="Changing financial numbering is a separate, master-only action — use Renumber on the row. It checks which numbers are still in use before allowing the change."
      />
      <Form className="erp-accounts-form"
        form={form} layout="vertical" initialValues={initial} onFinish={onSubmit}>
        <Form.Item label="Document Type">
          <Input value={row?.documentType || ''} disabled />
        </Form.Item>
        <Form.Item label="Financial Year">
          <Input value={row?.financialYear || '(all FYs)'} disabled />
        </Form.Item>
        <Form.Item label="Current Number">
          <Input value={row?.currentNumber ?? 0} disabled />
        </Form.Item>
        <Form.Item label="Prefix" name="prefix"
          rules={[{ required: true, message: 'Prefix is required' }, { max: 20 }]}>
          <Input maxLength={20} />
        </Form.Item>
        <Form.Item label="Padding (zero-pad width)" name="padding">
          <InputNumber min={0} max={12} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Reset Frequency" name="resetFrequency">
          <Select options={RESET_OPTIONS} />
        </Form.Item>
        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={busy}>Save</Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form>
    </Modal>
  );
}

// ── Renumber dialog ──────────────────────────────────────────────
//
// Loads usage on open so the operator never has to work out the safe value.
// Three states it must handle honestly:
//   verified          → we know the highest live number; the input has a floor
//   consumesSequence  → false for proforma_invoice, whose numbers come from
//                       the proformas themselves; changing this row does
//                       nothing, and the dialog says so instead of pretending
//   !verified         → no document table is mapped for this type, so we
//                       cannot show a floor. The document's own unique index
//                       is still the backstop, and we say that too.
function RenumberModal({ open, row, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!open || !row) return undefined;
    let cancelled = false;
    setLoading(true);
    setUsage(null);
    accountsApi.documentSequenceUsage(row.id)
      .then((u) => {
        if (cancelled) return;
        setUsage(u);
        setValue(u.suggested ?? u.currentNumber ?? 0);
      })
      .catch((e) => {
        if (cancelled) return;
        message.error(e?.response?.data?.error?.message || 'Could not read sequence usage.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, row]);

  const onSubmit = useCallback(async () => {
    if (!row) return;
    setBusy(true);
    try {
      const data = await accountsApi.renumberDocumentSequence(row.id, Number(value));
      message.success(data.changed
        ? `Current number set to ${data.currentNumber} (was ${data.previousNumber}).`
        : 'Current number is already that value — nothing changed.');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to renumber sequence.');
    } finally { setBusy(false); }
  }, [row, value, onSaved, onClose]);

  const floor = usage && usage.verified ? usage.minimumAllowed : 0;
  const nextPreview = row
    ? `${row.prefix}-${row.financialYear ? row.financialYear + '/' : ''}${String(Number(value) + 1).padStart(row.padding || 6, '0')}`
    : '';

  return (
    <Modal
      title={row ? `Renumber — ${row.documentType}` : 'Renumber'}
      open={open} onCancel={onClose} footer={null} destroyOnClose width={560}
      centered className="erp-accounts-modal"
    >
      {loading ? <Text type="secondary">Checking which numbers are in use…</Text> : null}

      {!loading && usage && !usage.consumesSequence ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="This sequence is not used for numbering"
          description={usage.note} />
      ) : null}

      {!loading && usage && usage.consumesSequence && !usage.verified ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Could not verify which numbers are in use"
          description={usage.note} />
      ) : null}

      {!loading && usage && usage.consumesSequence && usage.verified ? (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={`Highest number still in use: ${usage.highestInUse}`}
          description={
            usage.highestInUse === usage.currentNumber
              ? 'There is no gap — the counter already matches the last live document.'
              : `The counter is at ${usage.currentNumber}, so ${usage.currentNumber - usage.highestInUse} number(s) were issued to documents that no longer exist. Setting it to ${usage.minimumAllowed} makes the next document reuse the first of them.`
          } />
      ) : null}

      {!loading && usage ? (
        <div className="erp-accounts-form">
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Current number</Text>
            <div><Text strong style={{ fontSize: 18 }}>{usage.currentNumber}</Text></div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Set current number to</Text>
            <InputNumber
              value={value}
              min={floor}
              max={2147483647}
              precision={0}
              style={{ width: '100%' }}
              onChange={(v) => setValue(v ?? floor)}
              disabled={usage.consumesSequence === false}
            />
            {usage.verified ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Lowest safe value is {usage.minimumAllowed} — anything lower would
                collide with a document that still exists.
              </Text>
            ) : null}
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Next document will be</Text>
            <div><code className="erp-grid__code">{nextPreview}</code></div>
          </div>
          <Space>
            <Button type="primary" loading={busy} onClick={onSubmit}
              disabled={usage.consumesSequence === false}>
              Save
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Space>
        </div>
      ) : null}
    </Modal>
  );
}

export default function DocumentSequencesPage() {
  const { gridHandlers, refetch } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const [editing, setEditing] = useState(null);
  const [renumbering, setRenumbering] = useState(null);
  const [ensuring, setEnsuring] = useState(false);
  const { me } = useAuth();
  // Renumber is master-only server-side (requireMaster on the route); hiding
  // the button keeps the UI honest rather than offering a 403.
  const isMaster = me?.isMaster === true;

  const onEnsure = useCallback(async () => {
    setEnsuring(true);
    try {
      const data = await accountsApi.ensureDocumentSequences({});
      message.success(`Created ${data.createdCount} sequence(s) for FY ${data.financialYear}.`);
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to ensure sequences.');
    } finally { setEnsuring(false); }
  }, [refetch]);

  const columns = useMemo(() => ([
    { title: 'Document Type', dataIndex: 'documentType', key: 'documentType',
      sortField: 'document_type', sorter: true, width: 200, dataType: 'enum',
      groupable: true, groupField: 'document_type' },
    { title: 'Prefix', dataIndex: 'prefix', key: 'prefix',
      sortField: 'prefix', width: 100, dataType: 'string',
      render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'FY', dataIndex: 'financialYear', key: 'financialYear',
      sortField: 'financial_year', sorter: true, width: 100, dataType: 'string',
      render: (v) => v
        ? <code className="erp-grid__code">{v}</code>
        : <span className="erp-grid__muted">all FYs</span> },
    { title: 'Padding', dataIndex: 'padding', key: 'padding',
      width: 80, align: 'right', dataType: 'number' },
    { title: 'Current No', dataIndex: 'currentNumber', key: 'currentNumber',
      width: 110, align: 'right', dataType: 'number',
      render: (v) => <Text strong>{v ?? 0}</Text> },
    { title: 'Next No (preview)', key: 'preview',
      width: 200, dataType: 'string',
      render: (_v, row) => <code className="erp-grid__code">{previewNumber(row)}</code> },
    { title: 'Reset', dataIndex: 'resetFrequency', key: 'resetFrequency',
      width: 130, dataType: 'enum',
      filters: RESET_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
      filterField: 'reset_frequency',
      render: (v) => v ? <Tag color="default" style={{ marginInlineEnd: 0 }}>{v}</Tag> : '—' },
    { title: 'Active', dataIndex: 'isActive', key: 'isActive',
      width: 70, align: 'center', dataType: 'boolean',
      render: (v) => v
        ? <Tag color="success" style={{ marginInlineEnd: 0 }}>YES</Tag>
        : <Tag color="default" style={{ marginInlineEnd: 0 }}>NO</Tag> },
    { title: '', key: '__actions__', width: isMaster ? 96 : 56, align: 'center', fixed: 'right',
      render: (_v, row) => (
        <Space size={0}>
          <Tooltip title="Edit prefix, padding, reset frequency">
            <Button type="text" icon={<EditOutlined />}
                    onClick={(e) => { e.stopPropagation(); setEditing(row); }} />
          </Tooltip>
          {isMaster ? (
            <Tooltip title="Renumber — reuse a deleted document's number">
              <Button type="text" icon={<FieldNumberOutlined />}
                      onClick={(e) => { e.stopPropagation(); setRenumbering(row); }} />
            </Tooltip>
          ) : null}
        </Space>
      ) }
  ]), [isMaster]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Document Sequences</Title>
      <Alert type="info" banner showIcon style={{ marginBottom: 10 }}
        message="Configure per-document-type running numbers. Each business document draws from one sequence row per financial year." />
      <Space style={{ marginBottom: 10 }}>
        <Button type="primary" size="small" icon={<PlusOutlined />} loading={ensuring} onClick={onEnsure}>
          Create Missing Sequences for Current FY
        </Button>
      </Space>
      <ErpDataGrid
        title="Document Sequences"
        gridId="accounts-doc-sequences-v1"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        searchable
        searchPlaceholder="Search by document type / prefix / FY…"
        searchShortcut
        emptyTitle="No document sequences yet"
        emptyText="Click 'Create Missing Sequences' to seed the standard set."
        {...gridHandlers}
      />
      <EditSequenceModal
        open={!!editing} row={editing}
        onClose={() => setEditing(null)}
        onSaved={refetch}
      />
      <RenumberModal
        open={!!renumbering} row={renumbering}
        onClose={() => setRenumbering(null)}
        onSaved={refetch}
      />
      </div>
    </ErpMasterShell>
  );
}
